import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalPath } from '../../contexts/PortalContext';
import { useDesignCatalogueRealtime } from '../../hooks/useDesignCatalogueRealtime';
import {
  archiveDesignCatalogue,
  duplicateDesignCatalogue,
  listDesignCatalogueProjects,
  setDesignCatalogueStatus,
  type DesignCatalogueProject,
  type DesignCatalogueStatus,
} from '../../lib/designCatalogues';
import { designTemplateById } from '../../lib/designTemplates';

type ListFilter = 'active' | 'published' | 'draft' | 'archived';

function formatWhen(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export function DesignCataloguesListPage() {
  const base = usePortalPath();
  const navigate = useNavigate();
  const { authUser, profile } = useAuth();
  const [filter, setFilter] = useState<ListFilter>('active');
  const [rows, setRows] = useState<DesignCatalogueProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const actorEmail = authUser?.email ?? profile?.email ?? null;
  const actorId = authUser?.id ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listDesignCatalogueProjects({ status: filter }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load catalogues.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  useDesignCatalogueRealtime({
    onChange: () => {
      void load();
    },
  });

  async function handleArchive(id: string) {
    if (!window.confirm('Archive this catalogue? It will leave the active list.')) return;
    setBusyId(id);
    setError(null);
    try {
      await archiveDesignCatalogue(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Archive failed.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleRestore(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await setDesignCatalogueStatus(id, 'draft', { email: actorEmail, id: actorId });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Restore failed.');
    } finally {
      setBusyId(null);
    }
  }

  async function handlePublish(id: string, next: DesignCatalogueStatus) {
    setBusyId(id);
    setError(null);
    try {
      await setDesignCatalogueStatus(id, next, { email: actorEmail, id: actorId });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Status update failed.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDuplicate(id: string) {
    setBusyId(id);
    setError(null);
    try {
      const copy = await duplicateDesignCatalogue(id, { email: actorEmail, id: actorId });
      navigate(`${base}/catalogues/${copy.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Duplicate failed.');
      setBusyId(null);
    }
  }

  const filters: { id: ListFilter; label: string }[] = [
    { id: 'active', label: 'Active' },
    { id: 'draft', label: 'Drafts' },
    { id: 'published', label: 'Published' },
    { id: 'archived', label: 'Archived' },
  ];

  return (
    <div className="design-catalogues-page">
      <PageHeader
        title="Promo catalogues"
        description="Team library — everyone on the Design allowlist can open and edit. Pick a template, add photos and prices, export PDF."
        actions={
          <Link className="btn-primary" to={`${base}/catalogues/new`}>
            New catalogue
          </Link>
        }
      />

      <div className="design-catalogues-filters" role="tablist" aria-label="Catalogue filter">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            className={`design-catalogues-filters__btn${filter === f.id ? ' is-active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error ? <p className="form-error">{error}</p> : null}
      {loading ? <p className="page-lead">Loading catalogues…</p> : null}

      {!loading && rows.length === 0 ? (
        <section className="card design-catalogues-empty">
          <h2>No catalogues here</h2>
          <p>
            {filter === 'archived'
              ? 'Archived catalogues will show up in this tab.'
              : 'Create one from a preset template. Photos and product info auto-layout into pages.'}
          </p>
          {filter !== 'archived' ? (
            <Link className="btn-primary" to={`${base}/catalogues/new`}>
              Create first catalogue
            </Link>
          ) : null}
        </section>
      ) : null}

      {!loading && rows.length > 0 ? (
        <div className="design-catalogues-table-wrap">
          <table className="design-catalogues-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Template</th>
                <th>Status</th>
                <th>Updated</th>
                <th>By</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link className="design-catalogues-table__link" to={`${base}/catalogues/${row.id}`}>
                      {row.title || 'Untitled catalogue'}
                    </Link>
                  </td>
                  <td>{designTemplateById(row.template_id).label}</td>
                  <td>
                    <span className={`design-cat-status design-cat-status--${row.status}`}>
                      {row.status}
                    </span>
                  </td>
                  <td>{formatWhen(row.updated_at)}</td>
                  <td>{row.updated_by_email || row.created_by_email || '—'}</td>
                  <td className="design-catalogues-table__actions">
                    <Link className="btn-secondary btn-sm" to={`${base}/catalogues/${row.id}`}>
                      Open
                    </Link>
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      disabled={busyId === row.id}
                      onClick={() => void handleDuplicate(row.id)}
                    >
                      Duplicate
                    </button>
                    {row.status === 'archived' ? (
                      <button
                        type="button"
                        className="btn-ghost btn-sm"
                        disabled={busyId === row.id}
                        onClick={() => void handleRestore(row.id)}
                      >
                        Restore
                      </button>
                    ) : (
                      <>
                        {row.status === 'draft' ? (
                          <button
                            type="button"
                            className="btn-ghost btn-sm"
                            disabled={busyId === row.id}
                            onClick={() => void handlePublish(row.id, 'published')}
                          >
                            Publish
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-ghost btn-sm"
                            disabled={busyId === row.id}
                            onClick={() => void handlePublish(row.id, 'draft')}
                          >
                            Unpublish
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          disabled={busyId === row.id}
                          onClick={() => void handleArchive(row.id)}
                        >
                          Archive
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
